import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from './disposerBag'

export type WallSide = 'left' | 'right' | 'back' | 'front'

export type WallSpec =
  | { kind: 'solid'; material: BABYLON.Material }
  | { kind: 'glass'; material: BABYLON.Material; mullionMaterial: BABYLON.Material; mullionEvery?: number }

export interface RoomShellSpec {
  width: number
  height: number
  depth: number
  floorMaterial: BABYLON.Material
  ceilMaterial: BABYLON.Material
  /** Any side left out has no wall mesh at all (open doorway) — every current caller fills all four. */
  walls: Partial<Record<WallSide, WallSpec>>
}

export interface RoomShellResult {
  floor: BABYLON.Mesh
  ceil: BABYLON.Mesh
  walls: Partial<Record<WallSide, BABYLON.Mesh>>
}

const WALL_GEOMETRY: Record<WallSide, (w: number, d: number) => { width: number; x: number; z: number; rotY: number }> = {
  left: (w, d) => ({ width: d, x: -w / 2, z: 0, rotY: Math.PI / 2 }),
  right: (w, d) => ({ width: d, x: w / 2, z: 0, rotY: -Math.PI / 2 }),
  back: (w, d) => ({ width: w, x: 0, z: d / 2, rotY: Math.PI }),
  front: (w, d) => ({ width: w, x: 0, z: -d / 2, rotY: 0 }),
}

/**
 * One box room — floor, ceiling, and up to 4 walls, each independently
 * `solid` or floor-to-ceiling `glass` (with mullions). Replaces the near
 * identical wall/floor/ceiling loops SpacesStudio and Mountain Lot each
 * hand-rolled — a cabin with 2 glass walls and a sealed 4-solid-wall room
 * are now the same function called with a different `walls` config. See
 * `roomTemplate.ts`'s `buildTemplatedRoomShell` for the higher-level,
 * config-driven entry point most callers want instead of this directly.
 */
export function buildRoomShell(scene: BABYLON.Scene, bag: DisposerBag, spec: RoomShellSpec): RoomShellResult {
  const { width: W, height: H, depth: D } = spec

  const floor = bag.track(BABYLON.MeshBuilder.CreateGround('roomkit-floor', { width: W, height: D }, scene))
  floor.material = spec.floorMaterial
  floor.receiveShadows = true

  const ceil = bag.track(BABYLON.MeshBuilder.CreatePlane('roomkit-ceil', { width: W, height: D }, scene))
  ceil.rotation.x = -Math.PI / 2
  ceil.position.y = H
  ceil.material = spec.ceilMaterial
  ceil.isPickable = false

  const walls: Partial<Record<WallSide, BABYLON.Mesh>> = {}
  ;(Object.keys(spec.walls) as WallSide[]).forEach((side) => {
    const wallSpec = spec.walls[side]
    if (!wallSpec) return
    const { width, x, z, rotY } = WALL_GEOMETRY[side](W, D)
    const pane = bag.track(BABYLON.MeshBuilder.CreatePlane(`roomkit-wall-${side}`, { width, height: H }, scene))
    pane.position.set(x, H / 2, z)
    pane.rotation.y = rotY
    pane.material = wallSpec.material
    pane.receiveShadows = true
    walls[side] = pane

    if (wallSpec.kind === 'glass') {
      pane.isPickable = false
      const mullionEvery = wallSpec.mullionEvery ?? 1.35
      const mullionCount = Math.max(1, Math.round(width / mullionEvery))
      for (let i = 0; i <= mullionCount; i++) {
        const u = -width / 2 + (width / mullionCount) * i
        const mullion = bag.track(BABYLON.MeshBuilder.CreateBox(`roomkit-mullion-${side}`, { width: 0.06, height: H, depth: 0.06 }, scene))
        mullion.position.set(x + u * Math.cos(rotY), H / 2, z - u * Math.sin(rotY))
        mullion.material = wallSpec.mullionMaterial
      }
    }
  })

  return { floor, ceil, walls }
}
